// ============================================================================
// API HTTP core — a tiny typed fetch wrapper for the ViccoFitness JSON API.
// ----------------------------------------------------------------------------
// Everything goes through `request()`:
//   • base path is /api (proxied to FastAPI in dev, same-origin in prod)
//   • `credentials: "include"` so the signed `vf_session` cookie rides along
//   • JSON in / JSON out, with a typed ApiError carrying status + detail
//   • 401 is surfaced as ApiError(401) so the auth layer can redirect to /login
// No third-party HTTP client — fetch is enough and keeps the bundle lean.
// ============================================================================

/** Base path for all API calls. Same-origin; the dev server proxies it. */
export const API_BASE = "/api";

/** A failed API response: carries the HTTP status and the server's detail. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }

  /** True for an unauthenticated response (drives the redirect-to-login flow). */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export type Query = Record<
  string,
  string | number | boolean | null | undefined
>;

function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

interface RequestOptions {
  method?: string;
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Multipart upload. Sends a `FormData` body (e.g. a meal photo) — the browser
 * sets the `multipart/form-data` boundary header itself, so we deliberately do
 * NOT set Content-Type. Mirrors `request()`'s error/empty-body handling and the
 * `credentials: "include"` cookie posture so the same auth applies.
 */
export async function upload<T>(
  path: string,
  form: FormData,
  { method = "POST", query, signal }: { method?: string; query?: Query; signal?: AbortSignal } = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      body: form,
      credentials: "include",
      signal,
    });
  } catch (cause) {
    throw new ApiError(0, cause, "network error");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? (data as { detail: unknown }).detail
        : data;
    throw new ApiError(res.status, detail, `API ${res.status}`);
  }

  return data as T;
}

/**
 * Core request. Returns the parsed JSON body typed as `T`. Throws `ApiError`
 * on any non-2xx response, extracting FastAPI's `{detail}` when present.
 */
export async function request<T>(
  path: string,
  { method = "GET", query, body, signal }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: payload,
      credentials: "include",
      signal,
    });
  } catch (cause) {
    // Network-level failure (server down, CORS, offline).
    throw new ApiError(0, cause, "network error");
  }

  // 204 / empty bodies — return undefined as T.
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? (data as { detail: unknown }).detail
        : data;
    throw new ApiError(res.status, detail, `API ${res.status}`);
  }

  return data as T;
}

/** Convenience verb helpers. */
export const http = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>(path, { method: "GET", query, signal }),
  post: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>(path, { method: "POST", body, query }),
  put: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>(path, { method: "PUT", body, query }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  del: <T>(path: string, query?: Query) =>
    request<T>(path, { method: "DELETE", query }),
  upload: <T>(path: string, form: FormData, query?: Query, signal?: AbortSignal) =>
    upload<T>(path, form, { method: "POST", query, signal }),
};
